import { useEffect, useState, type ReactNode } from 'react';
import { Modal } from '../ui/Modal';
import { ErrorOverlay } from '../ui/ErrorOverlay';
import type { FinanceReviewAction } from '../../lib/applicationFormSubmissions';
import {
  DEFAULT_ACCOUNT_CREATION_FINANCE,
  EMPTY_ACCOUNT_CREATION_FORM,
  type AccountCreationFinanceData,
  type AccountCreationFormData,
} from '../../types/applicationForm';
import { CUSTOMER_PRICE_GROUP_OPTIONS } from '../../lib/accountCreationFinanceOptions';
import { useAccountCreationFieldOptions } from '../../lib/accountCreationFieldOptions';
import {
  lookupDeliveryScheduleByPostal,
  postalPrefixFromCode,
} from '../../lib/applicationFormSettings';
import { PAYMENT_TERMS_OPTIONS } from '../../lib/accountCreationUi';
import { AccountCreationForm } from './AccountCreationForm';

export type FinanceReviewConfirmInput = {
  customerCode?: string;
  reviewNotes?: string;
  financeData?: AccountCreationFinanceData;
  formData?: AccountCreationFormData;
  companyName?: string;
};

export type DeliveryAddressSummary = {
  outlet_name?: string;
  street?: string;
  building?: string;
  unit?: string;
  postal?: string;
};

type Props = {
  open: boolean;
  action: FinanceReviewAction | null;
  applicationNo?: string;
  initialCompanyName?: string;
  initialFormData?: AccountCreationFormData;
  initialCustomerCode?: string;
  initialFinanceData?: AccountCreationFinanceData | null;
  initialSalespersonCode?: string;
  /** Delivery postal from the application — used to auto-match territory. */
  deliveryPostal?: string;
  /** Customer-entered delivery address (shown on verify for reference). */
  deliveryAddress?: DeliveryAddressSummary;
  busy?: boolean;
  onClose: () => void;
  onConfirm: (input: FinanceReviewConfirmInput) => void;
};

const TITLES: Record<FinanceReviewAction, string> = {
  verified: 'Verify application',
  approved: 'Approve application (AR)',
  rejected: 'Reject application',
  kiv: 'Mark as KIV',
};

function Field({
  label,
  required,
  full,
  children,
}: {
  label: string;
  required?: boolean;
  full?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`af-form__field${full ? ' af-form__field--full' : ''}`}>
      <span>
        {label}
        {required ? <span className="af-form__req">*</span> : null}
      </span>
      {children}
    </label>
  );
}

function FinanceSelect({
  value,
  options,
  placeholder,
  onChange,
  disabled,
}: {
  value: string;
  options: { value: string; label: string }[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function formatDeliveryAddress(address?: DeliveryAddressSummary): string {
  if (!address) return '';
  const lines = [
    address.outlet_name?.trim() ? `Outlet: ${address.outlet_name.trim()}` : '',
    [address.street, address.building, address.unit].map((p) => p?.trim()).filter(Boolean).join(', '),
    address.postal?.trim() ? `Singapore ${address.postal.trim()}` : '',
  ].filter(Boolean);
  return lines.join('\n');
}

export function AccountCreationReviewModal({
  open,
  action,
  applicationNo,
  initialCompanyName = '',
  initialFormData,
  initialCustomerCode = '',
  initialFinanceData,
  initialSalespersonCode = '',
  deliveryPostal = '',
  deliveryAddress,
  busy = false,
  onClose,
  onConfirm,
}: Props) {
  const [customerCode, setCustomerCode] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [formDraft, setFormDraft] = useState<AccountCreationFormData>(EMPTY_ACCOUNT_CREATION_FORM);
  const [finance, setFinance] = useState<AccountCreationFinanceData>(DEFAULT_ACCOUNT_CREATION_FINANCE);
  const [error, setError] = useState<string | null>(null);
  const [routeHint, setRouteHint] = useState<string | null>(null);
  const { options: fieldOptions } = useAccountCreationFieldOptions();
  const deliveryAddressText = formatDeliveryAddress(deliveryAddress);

  useEffect(() => {
    if (!open) return;
    const baseForm: AccountCreationFormData = {
      ...EMPTY_ACCOUNT_CREATION_FORM,
      ...initialFormData,
      salesperson_code:
        initialFormData?.salesperson_code?.trim() || initialSalespersonCode.trim() || '',
    };
    setCompanyName(initialCompanyName.trim());
    setFormDraft(baseForm);
    setCustomerCode(initialCustomerCode.trim());
    setReviewNotes('');
    setFinance({
      ...DEFAULT_ACCOUNT_CREATION_FINANCE,
      ...initialFinanceData,
      salesperson_code:
        initialFinanceData?.salesperson_code?.trim() ||
        baseForm.salesperson_code ||
        initialSalespersonCode.trim(),
      cut_off_time: initialFinanceData?.cut_off_time?.trim() || '12:00 PM',
      division_code: '10',
      session: initialFinanceData?.session?.trim() || 'AM',
    });
    setError(null);
    setRouteHint(null);

    if (action !== 'verified') return;

    let cancelled = false;
    const postal = (deliveryPostal || baseForm.delivery_postal || '').trim();
    const prefix = postalPrefixFromCode(postal);

    void (async () => {
      if (!postal) return;
      try {
        const match = await lookupDeliveryScheduleByPostal(postal);
        if (cancelled || !match) return;
        setFinance((prev) => ({
          ...prev,
          territory_code: prev.territory_code.trim() || match.territory,
        }));
        const parts = [
          `Guideline for postal ${prefix}: schedule ${match.schedule || '—'} · ${match.territory || '—'}`,
        ];
        if (match.remarks.trim()) parts.push(match.remarks.trim());
        setRouteHint(parts.join('\n'));
      } catch {
        /* optional guideline only */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    action,
    initialCompanyName,
    initialFormData,
    initialCustomerCode,
    initialFinanceData,
    initialSalespersonCode,
    deliveryPostal,
  ]);

  const patchFinance = <K extends keyof AccountCreationFinanceData>(
    key: K,
    value: AccountCreationFinanceData[K],
  ) => {
    setFinance((prev) => ({ ...prev, [key]: value }));
  };

  const patchForm = <K extends keyof AccountCreationFormData>(
    key: K,
    value: AccountCreationFormData[K],
  ) => {
    setFormDraft((prev) => ({ ...prev, [key]: value }));
  };

  if (!action) return null;

  const handleSubmit = () => {
    setError(null);
    if (action === 'verified') {
      if (!companyName.trim()) {
        setError('Company name is required.');
        return;
      }
      if (!customerCode.trim()) {
        setError('Customer code is required.');
        return;
      }
      if (!finance.businessgroup_code.trim()) {
        setError('Business group code is required.');
        return;
      }
      if (!finance.customer_price_group.trim()) {
        setError('Customer price group is required.');
        return;
      }
      if (!finance.delivery_schedule.trim()) {
        setError('Delivery schedule is required.');
        return;
      }
      if (!finance.cut_off_time.trim()) {
        setError('Cut off time is required.');
        return;
      }
      if (!finance.session.trim()) {
        setError('Session is required.');
        return;
      }
      if (!finance.territory_code.trim()) {
        setError('Territory code is required.');
        return;
      }
      onConfirm({
        customerCode: customerCode.trim(),
        companyName: companyName.trim(),
        formData: formDraft,
        financeData: {
          ...finance,
          division_code: '10',
          salesperson_code:
            formDraft.salesperson_code?.trim() || finance.salesperson_code.trim(),
        },
      });
      return;
    }

    if (action === 'approved') {
      if (!companyName.trim()) {
        setError('Company name is required.');
        return;
      }
      if (!formDraft.finance_contact_name.trim()) {
        setError('Finance contact name is required.');
        return;
      }
      if (!formDraft.finance_contact_no.trim()) {
        setError('Finance contact number is required.');
        return;
      }
      if (!finance.payment_terms) {
        setError('Payment terms is required.');
        return;
      }
      onConfirm({
        customerCode: initialCustomerCode.trim() || customerCode.trim() || undefined,
        companyName: companyName.trim(),
        formData: formDraft,
        financeData: {
          ...DEFAULT_ACCOUNT_CREATION_FINANCE,
          ...initialFinanceData,
          payment_terms: finance.payment_terms,
        },
      });
      return;
    }

    if (action === 'rejected' && !reviewNotes.trim()) {
      setError('Rejection reason is required.');
      return;
    }
    onConfirm({
      reviewNotes: reviewNotes.trim() || undefined,
    });
  };

  return (
    <Modal
      title={TITLES[action]}
      open={open}
      onClose={busy ? () => {} : onClose}
      compactHeader
      wide={action === 'verified' || action === 'approved'}
    >
      <div className="af-review-modal">
        {applicationNo ? (
          <p className="af-review-modal__lead">
            Application <strong>{applicationNo}</strong>
          </p>
        ) : null}

        {action === 'verified' ? (
          <>
            <p className="af-settings-field__hint" style={{ marginBottom: 12 }}>
              Check and correct all applicant fields, then fill official use fields. Confirm sends
              this application to AR for approval.
            </p>
            <AccountCreationForm
              key={`${applicationNo}-verify`}
              initialCompanyName={initialCompanyName}
              initialFormData={initialFormData}
              requireDeclaration={false}
              requireSalespersonCode
              hideSubmit
              busy={busy}
              onValuesChange={(name, data) => {
                setCompanyName(name);
                setFormDraft(data);
              }}
              onSubmit={async () => undefined}
            />

            <h4 className="af-review-modal__section">For official use only</h4>
            <div className="af-form__grid af-form__grid--cols-2">
              <Field label="Payment terms" full>
                <select
                  value={finance.payment_terms}
                  onChange={(e) =>
                    patchFinance(
                      'payment_terms',
                      e.target.value as AccountCreationFinanceData['payment_terms'],
                    )
                  }
                  disabled={busy}
                >
                  <option value="">Select payment terms</option>
                  {(fieldOptions.payment_terms.length
                    ? fieldOptions.payment_terms
                    : PAYMENT_TERMS_OPTIONS
                  ).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Customer code" required>
                <input
                  type="text"
                  value={customerCode}
                  onChange={(e) => setCustomerCode(e.target.value)}
                  placeholder="e.g. F0123-A01 (mother account / outlet / NAV)"
                  disabled={busy}
                />
              </Field>
              <Field label="Customer price group" required>
                <FinanceSelect
                  value={finance.customer_price_group}
                  options={
                    fieldOptions.customer_price_group.length
                      ? fieldOptions.customer_price_group
                      : CUSTOMER_PRICE_GROUP_OPTIONS
                  }
                  placeholder="Select price group"
                  onChange={(value) => patchFinance('customer_price_group', value)}
                  disabled={busy}
                />
              </Field>
              <Field label="Business group code" required>
                <FinanceSelect
                  value={finance.businessgroup_code}
                  options={fieldOptions.businessgroup_code}
                  placeholder="Select business group"
                  onChange={(value) => patchFinance('businessgroup_code', value)}
                  disabled={busy}
                />
              </Field>
              <Field label="Division code" required>
                <input type="text" value="10" readOnly disabled={busy} />
              </Field>
              <Field label="Remarks" full>
                <textarea
                  className="af-review-modal__textarea"
                  rows={2}
                  value={finance.remarks}
                  onChange={(e) => patchFinance('remarks', e.target.value)}
                  disabled={busy}
                />
              </Field>
            </div>

            <h4 className="af-review-modal__section">Delivery information</h4>
            {deliveryAddressText ? (
              <p className="af-settings-field__hint" style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>
                <strong>Customer delivery address</strong>
                {'\n'}
                {deliveryAddressText}
              </p>
            ) : null}
            {routeHint ? (
              <p className="af-settings-field__hint" style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>
                {routeHint}
              </p>
            ) : null}
            <div className="af-form__grid">
              <Field label="Delivery schedule" required>
                <input
                  type="text"
                  value={finance.delivery_schedule}
                  onChange={(e) => patchFinance('delivery_schedule', e.target.value)}
                  placeholder="Enter schedule (see guideline above)"
                  disabled={busy}
                />
              </Field>
              <Field label="Cut off time" required>
                <input
                  type="text"
                  value={finance.cut_off_time}
                  onChange={(e) => patchFinance('cut_off_time', e.target.value)}
                  placeholder="12:00 PM"
                  disabled={busy}
                />
              </Field>
              <Field label="Session" required>
                <select
                  value={finance.session}
                  onChange={(e) => patchFinance('session', e.target.value)}
                  disabled={busy}
                >
                  <option value="">Select session</option>
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </Field>
              <Field label="Territory code" required>
                <input
                  type="text"
                  value={finance.territory_code}
                  onChange={(e) => patchFinance('territory_code', e.target.value)}
                  disabled={busy}
                />
              </Field>
            </div>
          </>
        ) : null}

        {action === 'approved' ? (
          <>
            <p className="af-settings-field__hint" style={{ marginBottom: 12 }}>
              AR may update company information, billing details, finance contact, and payment
              terms.
            </p>
            <h4 className="af-review-modal__section">Company information</h4>
            <div className="af-form__grid">
              <Field label="Company / trading name" required full>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  disabled={busy}
                />
              </Field>
              <Field label="Company registration no.">
                <input
                  type="text"
                  value={formDraft.company_reg_no}
                  onChange={(e) => patchForm('company_reg_no', e.target.value)}
                  disabled={busy}
                />
              </Field>
              <Field label="GST registration no.">
                <input
                  type="text"
                  value={formDraft.gst_reg_no}
                  onChange={(e) => patchForm('gst_reg_no', e.target.value)}
                  disabled={busy}
                />
              </Field>
            </div>

            <h4 className="af-review-modal__section">Finance contact</h4>
            <div className="af-form__grid af-form__grid--cols-2">
              <Field label="Finance contact name" required>
                <input
                  type="text"
                  value={formDraft.finance_contact_name}
                  onChange={(e) => patchForm('finance_contact_name', e.target.value)}
                  disabled={busy}
                />
              </Field>
              <Field label="Contact no." required>
                <input
                  type="text"
                  value={formDraft.finance_contact_no}
                  onChange={(e) => patchForm('finance_contact_no', e.target.value)}
                  disabled={busy}
                />
              </Field>
            </div>

            <h4 className="af-review-modal__section">Billing details</h4>
            <div className="af-form__grid">
              <Field label="Billing SOA">
                <select
                  value={formDraft.billing_soa}
                  onChange={(e) =>
                    patchForm('billing_soa', e.target.value as AccountCreationFormData['billing_soa'])
                  }
                  disabled={busy}
                >
                  <option value="individual">Individual</option>
                  <option value="group">Group</option>
                </select>
              </Field>
              <Field label="Billing street" full>
                <input
                  type="text"
                  value={formDraft.billing_street}
                  onChange={(e) => patchForm('billing_street', e.target.value)}
                  disabled={busy}
                />
              </Field>
              <Field label="Billing building">
                <input
                  type="text"
                  value={formDraft.billing_building}
                  onChange={(e) => patchForm('billing_building', e.target.value)}
                  disabled={busy}
                />
              </Field>
              <Field label="Billing unit">
                <input
                  type="text"
                  value={formDraft.billing_unit}
                  onChange={(e) => patchForm('billing_unit', e.target.value)}
                  disabled={busy}
                />
              </Field>
              <Field label="Billing postal">
                <input
                  type="text"
                  value={formDraft.billing_postal}
                  onChange={(e) => patchForm('billing_postal', e.target.value)}
                  disabled={busy}
                />
              </Field>
            </div>

            <h4 className="af-review-modal__section">Payment terms</h4>
            <div className="af-form__grid">
              <Field label="Payment terms" required>
                <select
                  value={finance.payment_terms}
                  onChange={(e) =>
                    patchFinance(
                      'payment_terms',
                      e.target.value as AccountCreationFinanceData['payment_terms'],
                    )
                  }
                  disabled={busy}
                >
                  <option value="">Select payment terms</option>
                  {(fieldOptions.payment_terms.length
                    ? fieldOptions.payment_terms
                    : PAYMENT_TERMS_OPTIONS
                  ).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </>
        ) : null}

        {action === 'rejected' ? (
          <Field label="Rejection reason" required>
            <textarea
              className="af-review-modal__textarea"
              rows={4}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Enter the reason for rejection"
              autoFocus
              disabled={busy}
            />
          </Field>
        ) : null}

        {action === 'kiv' ? (
          <Field label="Notes (optional)">
            <textarea
              className="af-review-modal__textarea"
              rows={3}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Enter follow-up notes for KIV"
              autoFocus
              disabled={busy}
            />
          </Field>
        ) : null}

        {error ? (
          <ErrorOverlay message={error} onClose={() => setError(null)} />
        ) : null}

        <div className="af-review-modal__actions">
          <button type="button" className="btn-secondary btn-sm" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={
              action === 'verified' || action === 'approved' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'
            }
            disabled={busy}
            onClick={handleSubmit}
          >
            {busy
              ? 'Saving…'
              : action === 'verified'
                ? 'Confirm verification'
                : action === 'approved'
                  ? 'Confirm approve'
                  : action === 'rejected'
                    ? 'Confirm reject'
                    : 'Save KIV'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
